import { useState, useEffect } from "react";
import { useNavigate } from "react-router";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { ArrowLeft } from "lucide-react";
import AxiosInstance from "@/utils/AxiosInstance";
import {useLocation } from "react-router";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

function isAxiosErrorLike(err: any): err is { isAxiosError: true; response?: { data?: any } } {
  return !!err && typeof err === "object" && (err as any).isAxiosError === true;
}

interface BloodPressureFlowProps {
  isOpen: boolean;
  onComplete: () => void;
}

const BloodPressureFlow = ({ isOpen, onComplete }: BloodPressureFlowProps) => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const alert = state?.alert;
  const [btnLoading, setBtnLoading] = useState(false);

  const primaryBtnClass = "bg-primary hover:bg-primary/90 text-white";
  const dangerBtnClass =
    "border-destructive text-destructive hover:bg-destructive/10";
  // const [showNursingNoteDialog, setShowNursingNoteDialog] = useState(false);
  const [currentStep, setCurrentStep] = useState<"type" | "medication_question" | "question1" | "low_question1" | "low_q2" | "low_q3" | "low_q4" | "low_q5" | "low_q6" | "low_q7" | "low_q8" | "low_q9" | "low_q10" | "low_q11" | "high_q2" | "high_q3" | "high_q4" | "high_q5" | "low_high_q2" | "low_high_q3" | "low_high_q4" | "low_high_q5" | "high_followup_q1" | "high_followup_q2" | "high_followup_q3" | "high_followup_q4" | "high_followup_q5" | "high_followup_q6" | "high_followup_q7" | "high_followup_q8" | "high_followup_q9" | "high_followup_q10" | "high_followup_q11" | "high_followup_q12">("type");
  const [bpType, setBpType] = useState<string>("");
  const [bpQuestion1, setBpQuestion1] = useState<string>("");
  const [medicationQuestion, setMedicationQuestion] = useState<string>("");
  const [medicationDetails, setMedicationDetails] = useState<string>("");
  const [highBpQuestion2, setHighBpQuestion2] = useState<string>("");
  const [highBpQuestion3, setHighBpQuestion3] = useState<string>("");
  const [highBpQuestion2Explanation, setHighBpQuestion2Explanation] = useState<string>("");
  const [highBpQuestion4, setHighBpQuestion4] = useState<Record<string, string>>({
    severeChestPain: "",
    nausea: "",
    vomiting: "",
    fainting: "",
    waterRetention: "",
    dizziness: "",
    fatigue: "",
    severeHeadache: "",
    blurredVision: "",
    confusion: "",
    severeAnxiety: "",
    seizure: "",
    unresponsiveness: ""
  });
  const [noHighSymptoms, setNoHighSymptoms] = useState<boolean>(false);
  const [highBpRecommendations, setHighBpRecommendations] = useState<Record<string, boolean>>({
    improveDiet: false,
    decreaseSalt: false,
    dailyExercise: false,
    weightLoss: false,
    reduceCaffeine: false,
    reduceAlcohol: false,
    reduceSmoking: false,
    reduceStress: false,
    takeMedication: false,
    monitorBpDaily: false,
    escalateToProvider: false,
    takeBpMorning: false
  });
  const [lowBpHighQuestion2, setLowBpHighQuestion2] = useState<string>("");
  const [lowBpHighQuestion3, setLowBpHighQuestion3] = useState<string>("");
  const [lowBpHighQuestion2Explanation, setLowBpHighQuestion2Explanation] = useState<string>("");
  const [lowBpHighQuestion4, setLowBpHighQuestion4] = useState<Record<string, string>>({
    rapidPulse: "",
    shallowBreathing: "",
    coldSkin: "",
    fainting: "",
    confusion: "",
    bleeding: ""
  });
  const [noSymptoms, setNoSymptoms] = useState<boolean>(false);
  const [lowBpRecommendations, setLowBpRecommendations] = useState<Record<string, boolean>>({
    increaseFluidIntake: false,
    drinkWater: false,
    standUpSlowly: false,
    elevateFeet: false,
    holdMedication: false,
    avoidCaffeine: false,
    avoidAlcohol: false,
    avoidSmoking: false,
    reduceStress: false,
    takeMedication: false,
    monitorBpDaily: false,
    escalateToProvider: false,
    takeBpMorning: false
  });
  const [lowBpQuestion1, setLowBpQuestion1] = useState<string>("");
  const [lowBpAnswers, setLowBpAnswers] = useState<Record<string, string>>({
    low_q2: "",
    low_q3: "",
    low_q4: "",
    low_q5: "",
    low_q6: "",
    low_q7: "",
    low_q8: "",
    low_q9: "",
    low_q10: "",
    low_q11: ""
  });
  const [lowBpAnswersExplanations, setLowBpAnswersExplanations] = useState<Record<string, string>>({});
  const [highBpFollowupAnswers, setHighBpFollowupAnswers] = useState<Record<string, string>>({
    high_followup_q1: "",
    high_followup_q2: "",
    high_followup_q3: "",
    high_followup_q4: "",
    high_followup_q5: "",
    high_followup_q6: "",
    high_followup_q7: "",
    high_followup_q8: "",
    high_followup_q9: "",
    high_followup_q10: "",
    high_followup_q11: "",
    high_followup_q12: ""
  });
  const [highBpFollowupExplanations, setHighBpFollowupExplanations] = useState<Record<string, string>>({});

  // Handle Enter key for all confirm buttons
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Enter" && !btnLoading && !e.shiftKey) {
        const target = e.target as HTMLElement;
        // Don't trigger if user is typing in input/textarea
        if (target.tagName !== "INPUT" && target.tagName !== "TEXTAREA") {
          e.preventDefault();
          
          // Trigger appropriate confirm handler based on current step
          switch (currentStep) {
            case "type":
              if (bpType) handleBpTypeConfirm();
              break;
            case "medication_question":
              handleMedicationQuestionConfirm();
              break;
            case "question1":
              handleBpQuestion1Confirm();
              break;
            case "high_q2":
              handleHighBpQuestion2Confirm();
              break;
            case "high_q3":
              handleHighBpQuestion3Confirm();
              break;
            case "high_q4":
              handleHighBpQuestion4Confirm();
              break;
            case "low_question1":
              handleLowBpQuestion1Confirm();
              break;
            case "low_high_q2":
              handleLowBpHighQuestion2Confirm();
              break;
            case "low_high_q3":
              handleLowBpHighQuestion3Confirm();
              break;
            case "low_high_q4":
              handleLowBpHighQuestion4Confirm();
              break;
            case "high_q5":
              // High BP recommendations screen
              const hasHighRecommendations = Object.values(highBpRecommendations).some(val => val === true);
              if (hasHighRecommendations) {
                handleHighBpRecommendationsConfirm();
              }
              break;
            case "low_high_q5":
              // Low BP recommendations screen
              const hasLowRecommendations = Object.values(lowBpRecommendations).some(val => val === true);
              if (hasLowRecommendations) {
                handleLowBpRecommendationsConfirm();
              }
              break;
            default:
              break;
          }
        }
      }
    };

    if (isOpen) {
      window.addEventListener("keydown", handleKeyDown);
      return () => window.removeEventListener("keydown", handleKeyDown);
    }
  }, [isOpen, currentStep, bpType, btnLoading, highBpRecommendations, lowBpRecommendations]);

  if (!isOpen) return null;

  // Function to build payload based on the flow taken
  const buildPayload = () => {
    const payload: any = {
      bpType: bpType,
      timestamp: new Date().toISOString(),
    };

    if (bpType === "high") {
      payload.highBloodPressure = {
        // Initial question
        isBloodPressureOver180: bpQuestion1,
        
        // Medication question
        medicationTaken: medicationQuestion,
        medicationDetails: medicationDetails || null,
      };

      if (bpQuestion1 === "yes") {
        // High BP "Yes" path
        payload.highBloodPressure.crisisPath = {
          hasExperiencedHypertensiveCrisis: highBpQuestion2,
          hypertensiveCrisisExplanation: highBpQuestion2Explanation || null,
          howDoYouFeel: highBpQuestion3 || null,
          symptoms: {
            severeChestPain: highBpQuestion4.severeChestPain,
            nausea: highBpQuestion4.nausea,
            vomiting: highBpQuestion4.vomiting,
            fainting: highBpQuestion4.fainting,
            waterRetention: highBpQuestion4.waterRetention,
            dizziness: highBpQuestion4.dizziness,
            fatigue: highBpQuestion4.fatigue,
            severeHeadache: highBpQuestion4.severeHeadache,
            blurredVision: highBpQuestion4.blurredVision,
            confusion: highBpQuestion4.confusion,
            severeAnxiety: highBpQuestion4.severeAnxiety,
            seizure: highBpQuestion4.seizure,
            unresponsiveness: highBpQuestion4.unresponsiveness,
            noSymptoms: noHighSymptoms,
          },
          recommendations: highBpRecommendations,
        };
      } else {
        // High BP "No" path - follow-up questions
        payload.highBloodPressure.followUpPath = {
          unusualDailyRoutine: highBpFollowupAnswers.high_followup_q1,
          unusualDailyRoutineExplanation: highBpFollowupExplanations.high_followup_q1 || null,
          tookBpAfterActivity: highBpFollowupAnswers.high_followup_q2,
          activityType: highBpFollowupExplanations.high_followup_q2 || null,
          feelingStressAnxietyPain: highBpFollowupAnswers.high_followup_q3,
          stressAnxietyPainExplanation: highBpFollowupExplanations.high_followup_q3 || null,
          eatenMoreSaltyFoods: highBpFollowupAnswers.high_followup_q4,
          saltyFoodsExplanation: highBpFollowupExplanations.high_followup_q4 || null,
          consumedAlcohol: highBpFollowupAnswers.high_followup_q5,
          alcoholDetails: highBpFollowupExplanations.high_followup_q5 || null,
          beenSmoking: highBpFollowupAnswers.high_followup_q6,
          smokingDetails: highBpFollowupExplanations.high_followup_q6 || null,
          usedAntiInflammatory: highBpFollowupAnswers.high_followup_q7,
          antiInflammatoryDetails: highBpFollowupExplanations.high_followup_q7 || null,
          onNewMedications: highBpFollowupAnswers.high_followup_q8,
          newMedicationsExplanation: highBpFollowupExplanations.high_followup_q8 || null,
          gainedWeight: highBpFollowupAnswers.high_followup_q9,
          weightGainDetails: highBpFollowupExplanations.high_followup_q9 || null,
          exerciseAmount: highBpFollowupAnswers.high_followup_q10 || null,
          changesToExerciseProgram: highBpFollowupAnswers.high_followup_q11,
          exerciseProgramExplanation: highBpFollowupExplanations.high_followup_q11 || null,
          howDoYouFeel: highBpFollowupAnswers.high_followup_q12 || null,
          recommendations: highBpRecommendations,
        };
      }
    } else if (bpType === "low") {
      payload.lowBloodPressure = {
        // Initial question
        isBloodPressureUnder90: lowBpQuestion1,
        
        // Medication question
        medicationTaken: medicationQuestion,
        medicationDetails: medicationDetails || null,
      };

      if (lowBpQuestion1 === "yes") {
        // Low BP "Yes" path
        payload.lowBloodPressure.crisisPath = {
          hasExperiencedHypotensiveCrisis: lowBpHighQuestion2,
          hypotensiveCrisisExplanation: lowBpHighQuestion2Explanation || null,
          howDoYouFeel: lowBpHighQuestion3 || null,
          symptoms: {
            rapidPulse: lowBpHighQuestion4.rapidPulse,
            shallowBreathing: lowBpHighQuestion4.shallowBreathing,
            coldSkin: lowBpHighQuestion4.coldSkin,
            fainting: lowBpHighQuestion4.fainting,
            confusion: lowBpHighQuestion4.confusion,
            bleeding: lowBpHighQuestion4.bleeding,
            noSymptoms: noSymptoms,
          },
        };
      } else {
        // Low BP "No" path - follow-up questions
        payload.lowBloodPressure.followUpPath = {
          unusualDailyRoutine: lowBpAnswers.low_q2,
          unusualDailyRoutineExplanation: lowBpAnswersExplanations.low_q2 || null,
          takenBpLoweringMedication: lowBpAnswers.low_q3,
          bpLoweringMedicationDetails: lowBpAnswersExplanations.low_q3 || null,
          drinkingEnoughFluid: lowBpAnswers.low_q4,
          fluidIntakeExplanation: lowBpAnswersExplanations.low_q4 || null,
          couldBeDehydrated: lowBpAnswers.low_q5,
          dehydrationExplanation: lowBpAnswersExplanations.low_q5 || null,
          onNewMedications: lowBpAnswers.low_q6,
          newMedicationsExplanation: lowBpAnswersExplanations.low_q6 || null,
          consumedAlcohol: lowBpAnswers.low_q7,
          alcoholDetails: lowBpAnswersExplanations.low_q7 || null,
          beenSmoking: lowBpAnswers.low_q8,
          smokingDetails: lowBpAnswersExplanations.low_q8 || null,
          usedAntiInflammatory: lowBpAnswers.low_q9,
          antiInflammatoryDetails: lowBpAnswersExplanations.low_q9 || null,
          lostWeight: lowBpAnswers.low_q10,
          weightLossDetails: lowBpAnswersExplanations.low_q10 || null,
          howDoYouFeel: lowBpAnswers.low_q11 || null,
          recommendations: lowBpRecommendations,
        };
      }
    }

    return payload;
  };

  const handleBpTypeConfirm = () => {
    if (!bpType) {
      toast.error("Please select a BP type");
      return;
    }
    
    // For low BP, go to low_question1 first, for high BP go directly to question1
    if (bpType === "low") {
      setCurrentStep("low_question1");
    } else {
      setCurrentStep("question1");
    }
  };

  const handleMedicationQuestionConfirm = () => {
    // If "yes" is selected, require medication details
    if (medicationQuestion === "yes" && !medicationDetails?.trim()) {
      toast.error("Please enter BP meds taken today, time taken, dosage taken");
      return;
    }
    
    if (bpType === "high") {
      // For high BP, after medication question, go to follow-up questions
      setCurrentStep("high_followup_q1");
    } else {
      // Low BP flow - continue based on low_question1 answer
      if (lowBpQuestion1 === "yes") {
        // If Yes, go to low BP specific questions
        setCurrentStep("low_high_q2");
      } else {
        // If No, go to next question
        setCurrentStep("low_q2");
      }
    }
  };

  const handleLowBpQuestion1Confirm = () => {
    if (!lowBpQuestion1) {
      toast.error("Please select an option");
      return;
    }
    
    if (lowBpQuestion1 === "yes") {
      setCurrentStep("low_high_q2");
    } else {
      setCurrentStep("low_q2");
    }
  };

  const handleLowBpHighQuestion2Confirm = () => {
    // If "yes" is selected, require explanation
    if (lowBpHighQuestion2 === "yes" && !lowBpHighQuestion2Explanation?.trim()) {
      toast.error("Please provide an explanation for your answer");
      return;
    }
    
    setCurrentStep("low_high_q3");
  };

  const handleLowBpHighQuestion3Confirm = () => {
    if (!lowBpHighQuestion3?.trim()) {
      toast.error("Please describe how you feel");
      return;
    }
    
    setCurrentStep("low_high_q4");
  };

  const handleLowBpHighQuestion4Confirm = async () => {
    // Check if at least one symptom question is answered with "yes" or noSymptoms is checked
    const hasAnyYesAnswer = Object.values(lowBpHighQuestion4).some(val => val === "yes");
    
    if (!noSymptoms && !hasAnyYesAnswer) {
      // This is fine - all can be "no"
    }
    
    // Build and log payload
    const payload = buildPayload();
    console.log("Low BP Crisis Path Submit - Payload:", payload);

    const isSuccessfulPayload = (d: any) =>
      !!d &&
      (d?.success === true ||
        typeof d?.alertId === "string" ||
        typeof d?.timestamp === "string");

    try {
      setBtnLoading(true);
      const res = await AxiosInstance().post("/triage-forms/blood-pressure", {
        ...payload,
        alertId: alert?._id,
      });
      const data: any = res?.data;

      if (isSuccessfulPayload(data)) {
        toast.success(data?.message ?? "Saved successfully");
        onComplete();
        return;
      }

      toast.error(data?.message ?? "Something went wrong");
    } catch (e: any) {
      // Backend sometimes returns useful data on non-2xx; treat as success if payload looks valid.
      const responseData = isAxiosErrorLike(e) ? e.response?.data : undefined;
      if (isSuccessfulPayload(responseData)) {
        toast.success(responseData?.message ?? "Saved successfully");
        onComplete();
        return;
      }
      toast.error(
        responseData?.message ??
          e?.response?.data?.message ??
          "Something went wrong",
      );
      console.log(e);
    } finally {
      setBtnLoading(false);
    }
  };

  // const handleNursingNoteResponse = (response: "yes" | "no") => {
  //   setShowNursingNoteDialog(false);

  //   if (response === "yes") {
  //     // Navigate to patient profile
  //     const patientId = alert?.patientId?._id || alert?.patientId;
  //     if (patientId) {
  //       navigate(`/patients/${patientId}?tab=nursing-notes`);
  //     } else {
  //       // Fallback to alerts if patient ID not found
  //       navigate("/alerts");
  //     }
  //   } else {
  //     navigate("/alerts");
  //   }
  // };

  // Handle Enter key for Nursing Note dialog
  // useEffect(() => {
  //   const handleKeyDown = (e: KeyboardEvent) => {
  //     if (e.key === "Enter" && showNursingNoteDialog && !e.shiftKey) {
  //       e.preventDefault();
  //       // Default to "Yes" when Enter is pressed
  //       handleNursingNoteResponse("yes");
  //     }
  //   };

  //   if (showNursingNoteDialog) {
  //     window.addEventListener("keydown", handleKeyDown);
  //     return () => window.removeEventListener("keydown", handleKeyDown);
  //   }
  // }, [showNursingNoteDialog]);

  const handleLowBpRecommendationsConfirm = async () => {
    // Check if at least one recommendation is selected
    const hasAnyRecommendation = Object.values(lowBpRecommendations).some(val => val === true);
    
    if (!hasAnyRecommendation) {
      toast.error("Please select at least one recommendation");
      return;
    }
    
    // Build and log payload
    const payload = buildPayload();
    console.log("Low BP Recommendations Submit - Payload:", payload);

    const isSuccessfulPayload = (d: any) =>
      !!d &&
      (d?.success === true ||
        typeof d?.alertId === "string" ||
        typeof d?.timestamp === "string");

    try {
      setBtnLoading(true);
      const res = await AxiosInstance().post("/triage-forms/blood-pressure", {
        ...payload,
        alertId: alert?._id,
      });
      const data: any = res?.data;
      if (isSuccessfulPayload(data)) {
        toast.success(data?.message ?? "Saved successfully");
        onComplete();
        return;
      }
      toast.error(data?.message ?? "Something went wrong");
    } catch (e: any) {
      const responseData = isAxiosErrorLike(e) ? e.response?.data : undefined;
      if (isSuccessfulPayload(responseData)) {
        toast.success(responseData?.message ?? "Saved successfully");
        onComplete();
        return;
      }
      toast.error(
        responseData?.message ??
          e?.response?.data?.message ??
          "Something went wrong",
      );
      console.log(e);
    } finally {
      setBtnLoading(false);
    }
  };

  const handleLowBpQuestion = (questionKey: string, nextStep: typeof currentStep | "complete") => {
    // For low_q11 (How do you feel?), require textarea input
    if (questionKey === "low_q11") {
      if (!lowBpAnswers[questionKey]?.trim()) {
        toast.error("Please describe how you feel");
        return;
      }
    } else if (questionKey === "low_q4") {
      // For low_q4, if "no" is selected, require explanation
      if (lowBpAnswers[questionKey] === "no" && !lowBpAnswersExplanations[questionKey]?.trim()) {
        toast.error("Please provide an explanation for your answer");
        return;
      }
    } else {
      // If "yes" is selected, require explanation
      if (lowBpAnswers[questionKey] === "yes" && !lowBpAnswersExplanations[questionKey]?.trim()) {
        toast.error("Please provide an explanation for your answer");
        return;
      }
    }
    
    if (nextStep === "complete") {
      // For low_q11, go to recommendations (no terms check here, terms are on recommendations page)
      if (questionKey === "low_q11") {
        setCurrentStep("low_high_q5");
      } else {
        // If we came from high BP "No" path, go to high BP recommendations
        if (bpType === "high" && bpQuestion1 === "no") {
          setCurrentStep("high_q5");
        } 
        // If we came from low BP "No" path, go to low BP recommendations
        else if (bpType === "low" && lowBpQuestion1 === "no") {
          setCurrentStep("low_high_q5");
        } 
        else {
          onComplete();
        }
      }
    } else {
      setCurrentStep(nextStep);
    }
  };

  const handleBpQuestion1Confirm = () => {
    if (bpQuestion1 === "yes") {
      // If Yes, go directly to high_q2 (Have you ever experienced a hypertensive crisis?)
      setCurrentStep("high_q2");
    } else {
      // If No, go to medication question first, then follow-up questions
      setCurrentStep("medication_question");
    }
  };

  const handleHighBpRecommendationsConfirm = async () => {
    // Check if at least one recommendation is selected
    const hasAnyRecommendation = Object.values(highBpRecommendations).some(val => val === true);
    
    if (!hasAnyRecommendation) {
      toast.error("Please select at least one recommendation");
      return;
    }
    
    // Build and log payload
    const payload = buildPayload();
    console.log("High BP Recommendations Submit - Payload:", payload);

    const isSuccessfulPayload = (d: any) =>
      !!d &&
      (d?.success === true ||
        typeof d?.alertId === "string" ||
        typeof d?.timestamp === "string");

    try {
      setBtnLoading(true);
      const res = await AxiosInstance().post("/triage-forms/blood-pressure", {
        ...payload,
        alertId: alert?._id,
      });
      const data: any = res?.data;
      if (isSuccessfulPayload(data)) {
        toast.success(data?.message ?? "Saved successfully");
        onComplete();
        return;
      }
      toast.error(data?.message ?? "Something went wrong");
    } catch (e: any) {
      const responseData = isAxiosErrorLike(e) ? e.response?.data : undefined;
      if (isSuccessfulPayload(responseData)) {
        toast.success(responseData?.message ?? "Saved successfully");
        onComplete();
        return;
      }
      toast.error(
        responseData?.message ??
          e?.response?.data?.message ??
          "Something went wrong",
      );
      console.log(e);
    } finally {
      setBtnLoading(false);
    }
    
    // onComplete();
  };

  const handleHighBpQuestion2Confirm = () => {
    if (!highBpQuestion2) {
      toast.error("Please select an option");
      return;
    }
    
    // If "yes" is selected, require explanation
    if (highBpQuestion2 === "yes" && !highBpQuestion2Explanation?.trim()) {
      toast.error("Please provide an explanation for your answer");
      return;
    }
    
    setCurrentStep("high_q3");
  };

  const handleHighBpQuestion3Confirm = () => {
    if (!highBpQuestion3?.trim()) {
      toast.error("Please describe how you feel");
      return;
    }
    
    setCurrentStep("high_q4");
  };

  const handleHighBpQuestion4Confirm = async () => {
    // Check if at least one symptom question is answered with "yes" or noHighSymptoms is checked
    const hasAnyYesAnswer = Object.values(highBpQuestion4).some(val => val === "yes");
    
    if (!noHighSymptoms && !hasAnyYesAnswer) {
      // This is fine - all can be "no"
    }
    
    // Build and log payload
    const payload = buildPayload();
    console.log("High BP Crisis Path Submit - Payload:", payload);

    
    const isSuccessfulPayload = (d: any) =>
      !!d &&
      (d?.success === true ||
        typeof d?.alertId === "string" ||
        typeof d?.timestamp === "string");

    try {
      setBtnLoading(true);
      const res = await AxiosInstance().post("/triage-forms/blood-pressure", {
        ...payload,
        alertId: alert?._id,
      });
      const data: any = res?.data;
      if (isSuccessfulPayload(data)) {
        toast.success(data?.message ?? "Saved successfully");
        onComplete();
        return;
      }
      toast.error(data?.message ?? "Something went wrong");
    } catch (e: any) {
      const responseData = isAxiosErrorLike(e) ? e.response?.data : undefined;
      if (isSuccessfulPayload(responseData)) {
        toast.success(responseData?.message ?? "Saved successfully");
        onComplete();
        return;
      }
      toast.error(
        responseData?.message ??
          e?.response?.data?.message ??
          "Something went wrong",
      );
      console.log(e);
    } finally {
      setBtnLoading(false);
    }
    

    
    // Go to recommendations
    // setCurrentStep("high_q5");
  };

  const handleHighBpFollowupQuestion = (questionKey: string, nextStep: typeof currentStep) => {
    // For high_followup_q10 (How much exercise have you had?) and high_followup_q12 (How do you feel?), require textarea input
    if (questionKey === "high_followup_q10" || questionKey === "high_followup_q12") {
      if (!highBpFollowupAnswers[questionKey]?.trim()) {
        toast.error(questionKey === "high_followup_q10" ? "Please describe how much exercise you have had" : "Please describe how you feel");
        return;
      }
    } else {
      // If "yes" is selected, require explanation
      if (highBpFollowupAnswers[questionKey] === "yes" && !highBpFollowupExplanations[questionKey]?.trim()) {
        toast.error("Please provide an explanation for your answer");
        return;
      }
    }
    
    // Go to next step (for high_followup_q12, nextStep will be "high_q5")
    setCurrentStep(nextStep);
  };


  const handleBack = () => {
    // Special case for medication_question - depends on BP type
    if (currentStep === "medication_question") {
      if (bpType === "low") {
        setCurrentStep("low_question1");
      } else {
        setCurrentStep("question1");
      }
      return;
    }
    
    // Special cases for steps that go back to medication_question
    if (currentStep === "low_q2" || currentStep === "high_followup_q1") {
      setCurrentStep("medication_question");
      return;
    }
    
    // Special case for low_high_q2 - goes back to low_question1
    if (currentStep === "low_high_q2") {
      setCurrentStep("low_question1");
      return;
    }
    
    const stepMap: Record<string, typeof currentStep> = {
      "question1": "type",
      "low_question1": "type",
      "high_q2": "question1",
      "high_q3": "high_q2",
      "high_q4": "high_q3",
      "high_q5": "high_q4",
      "low_high_q3": "low_high_q2" as typeof currentStep,
      "low_high_q4": "low_high_q3",
      "low_high_q5": "low_q11",
      "low_q4": "low_q3",
      "low_q5": "low_q4",
      "low_q6": "low_q5",
      "low_q7": "low_q6",
      "low_q8": "low_q7",
      "low_q9": "low_q8",
      "low_q10": "low_q9",
      "low_q11": "low_q10",
      "high_followup_q3": "high_followup_q2",
      "high_followup_q4": "high_followup_q3",
      "high_followup_q5": "high_followup_q4",
      "high_followup_q6": "high_followup_q5",
      "high_followup_q7": "high_followup_q6",
      "high_followup_q8": "high_followup_q7",
      "high_followup_q9": "high_followup_q8",
      "high_followup_q10": "high_followup_q9",
      "high_followup_q11": "high_followup_q10",
      "high_followup_q12": "high_followup_q11",
    };
    
    
    const previousStep = stepMap[currentStep];
    if (previousStep) {
      setCurrentStep(previousStep);
    }
  };

  return (
    <div className="space-y-6 mt-6">
      {/* BP Type Selection */}
      {currentStep === "type" && (
        <Card>
          <CardHeader>
            <CardTitle>Select BP Type</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <RadioGroup value={bpType} onValueChange={setBpType} className="space-y-3">
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="high" id="bp-high" />
                  <Label htmlFor="bp-high" className="cursor-pointer">High Blood Pressure</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="low" id="bp-low" />
                  <Label htmlFor="bp-low" className="cursor-pointer">Low Blood Pressure</Label>
                </div>
              </RadioGroup>
              <div className="flex justify-end gap-3 pt-4">
                <Button variant="outline" onClick={() => navigate("/alerts")}>
                  Cancel
                </Button>
                <Button onClick={handleBpTypeConfirm} className={primaryBtnClass}>
                  Confirm
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Medication Question */}
      {currentStep === "medication_question" && (
        <Card>
          <CardHeader>
            <CardTitle>Have you taken your prescribed blood pressure medication for today?</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <RadioGroup value={medicationQuestion} onValueChange={setMedicationQuestion} className="flex flex-row gap-6">
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="yes" id="medication-yes" />
                  <Label htmlFor="medication-yes" className="cursor-pointer">Yes</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="no" id="medication-no" />
                  <Label htmlFor="medication-no" className="cursor-pointer">No</Label>
                </div>
              </RadioGroup>
              {medicationQuestion === "yes" && (
                <div className="space-y-2">
                  <Label htmlFor="medication-details">Please enter BP meds taken today, time taken, dosage taken</Label>
                  <Textarea
                    id="medication-details"
                    placeholder="Please enter BP meds taken today, time taken, dosage taken..."
                    value={medicationDetails}
                    onChange={(e) => setMedicationDetails(e.target.value)}
                    className="min-h-[120px]"
                  />
                </div>
              )}
              <div className="flex justify-between gap-3 pt-4">
                <Button variant="outline" onClick={handleBack} className="flex items-center gap-2">
                  <ArrowLeft className="h-4 w-4" />
                  Back
                </Button>
                <div className="flex gap-3">
                  <Button variant="outline" onClick={() => navigate("/alerts")}>Cancel</Button>
                  <Button onClick={handleMedicationQuestionConfirm} className={primaryBtnClass}>
                    Confirm
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* BP Question 1 */}
      {currentStep === "question1" && (
        <Card>
          <CardHeader>
            <CardTitle>Is blood pressure &gt;180 systolically and/or &gt;110 diastolically ?</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <RadioGroup value={bpQuestion1} onValueChange={setBpQuestion1} className="flex flex-row gap-6">
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="yes" id="bp-q1-yes" />
                  <Label htmlFor="bp-q1-yes" className="cursor-pointer">Yes</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="no" id="bp-q1-no" />
                  <Label htmlFor="bp-q1-no" className="cursor-pointer">No</Label>
                </div>
              </RadioGroup>
              
              {/* Show warning when Yes is selected */}
              {bpQuestion1 === "yes" && (
                <div className="bg-red-500 text-white p-3 rounded-md">
                  <p className="text-sm font-medium">Note to Admin:</p>
                  <p className="text-sm mt-1">This patient may be experiencing a Hypertensive Crisis which can be a serious, life-threatening condition.</p>
                </div>
              )}
              
              <div className="flex justify-between gap-3 pt-4">
                <Button variant="outline" onClick={handleBack} className="flex items-center gap-2">
                  <ArrowLeft className="h-4 w-4" />
                  Back
                </Button>
                <div className="flex gap-3">
                  <Button variant="outline" onClick={() => navigate("/alerts")}>
                    Cancel
                  </Button>
                  <Button onClick={handleBpQuestion1Confirm} className={primaryBtnClass}>
                    Confirm
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* High BP Question 2 */}
      {currentStep === "high_q2" && (
        <Card>
          <CardHeader>
            <CardTitle>Have you ever experienced a hypertensive crisis in the past?</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <RadioGroup value={highBpQuestion2} onValueChange={setHighBpQuestion2} className="flex flex-row gap-6">
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="yes" id="high-q2-yes" />
                  <Label htmlFor="high-q2-yes" className="cursor-pointer">Yes</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="no" id="high-q2-no" />
                  <Label htmlFor="high-q2-no" className="cursor-pointer">No</Label>
                </div>
              </RadioGroup>
              {highBpQuestion2 === "yes" && (
                <div className="space-y-2">
                  <Label htmlFor="high-q2-explanation">Please explain:</Label>
                  <Textarea
                    id="high-q2-explanation"
                    placeholder="Please provide details..."
                    value={highBpQuestion2Explanation}
                    onChange={(e) => setHighBpQuestion2Explanation(e.target.value)}
                    className="min-h-[100px]"
                  />
                </div>
              )}
              <div className="flex justify-between gap-3 pt-4">
                <Button variant="outline" onClick={handleBack} className="flex items-center gap-2">
                  <ArrowLeft className="h-4 w-4" />
                  Back
                </Button>
                <div className="flex gap-3">
                  <Button variant="outline" onClick={() => navigate("/alerts")}>Cancel</Button>
                  <Button onClick={handleHighBpQuestion2Confirm} className={primaryBtnClass}>
                    Confirm
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* High BP Question 3 */}
      {currentStep === "high_q3" && (
        <Card>
          <CardHeader>
            <CardTitle>How do you feel?</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="high-q3-feelings">Please describe how you feel:</Label>
                <Textarea
                  id="high-q3-feelings"
                  placeholder="Please describe how you feel..."
                  value={highBpQuestion3}
                  onChange={(e) => setHighBpQuestion3(e.target.value)}
                  className="min-h-[120px]"
                />
              </div>
              <div className="flex justify-between gap-3 pt-4">
                <Button variant="outline" onClick={handleBack} className="flex items-center gap-2">
                  <ArrowLeft className="h-4 w-4" />
                  Back
                </Button>
                <div className="flex gap-3">
                  <Button variant="outline" onClick={() => navigate("/alerts")}>Cancel</Button>
                  <Button onClick={handleHighBpQuestion3Confirm} className={primaryBtnClass}>
                    Confirm
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* High BP Question 4 (Symptoms Question) */}
      {currentStep === "high_q4" && (
        <Card>
          <CardHeader>
            <CardTitle>Are you currently experiencing any of the following?</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {/* Severe chest pain */}
              <div className="flex items-center justify-between">
                <Label className="text-base font-medium">Severe chest pain</Label>
                <RadioGroup 
                  value={highBpQuestion4.severeChestPain} 
                  onValueChange={(value) => setHighBpQuestion4({...highBpQuestion4, severeChestPain: value})} 
                  className="flex flex-row gap-6"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="yes" id="severe-chest-pain-yes" />
                    <Label htmlFor="severe-chest-pain-yes" className="cursor-pointer">Yes</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="no" id="severe-chest-pain-no" />
                    <Label htmlFor="severe-chest-pain-no" className="cursor-pointer">No</Label>
                  </div>
                </RadioGroup>
              </div>

              {/* Nausea (new onset) */}
              <div className="flex items-center justify-between">
                <Label className="text-base font-medium">Nausea (new onset)</Label>
                <RadioGroup 
                  value={highBpQuestion4.nausea} 
                  onValueChange={(value) => setHighBpQuestion4({...highBpQuestion4, nausea: value})} 
                  className="flex flex-row gap-6"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="yes" id="nausea-yes" />
                    <Label htmlFor="nausea-yes" className="cursor-pointer">Yes</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="no" id="nausea-no" />
                    <Label htmlFor="nausea-no" className="cursor-pointer">No</Label>
                  </div>
                </RadioGroup>
              </div>

              {/* Vomiting (new onset) */}
              <div className="flex items-center justify-between">
                <Label className="text-base font-medium">Vomiting (new onset)</Label>
                <RadioGroup 
                  value={highBpQuestion4.vomiting} 
                  onValueChange={(value) => setHighBpQuestion4({...highBpQuestion4, vomiting: value})} 
                  className="flex flex-row gap-6"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="yes" id="vomiting-yes" />
                    <Label htmlFor="vomiting-yes" className="cursor-pointer">Yes</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="no" id="vomiting-no" />
                    <Label htmlFor="vomiting-no" className="cursor-pointer">No</Label>
                  </div>
                </RadioGroup>
              </div>

              {/* Fainting (new onset) */}
              <div className="flex items-center justify-between">
                <Label className="text-base font-medium">Fainting (new onset)</Label>
                <RadioGroup 
                  value={highBpQuestion4.fainting} 
                  onValueChange={(value) => setHighBpQuestion4({...highBpQuestion4, fainting: value})} 
                  className="flex flex-row gap-6"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="yes" id="fainting-high-yes" />
                    <Label htmlFor="fainting-high-yes" className="cursor-pointer">Yes</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="no" id="fainting-high-no" />
                    <Label htmlFor="fainting-high-no" className="cursor-pointer">No</Label>
                  </div>
                </RadioGroup>
              </div>

              {/* Increased water retention */}
              <div className="flex items-center justify-between">
                <Label className="text-base font-medium">Increased water retention</Label>
                <RadioGroup 
                  value={highBpQuestion4.waterRetention} 
                  onValueChange={(value) => setHighBpQuestion4({...highBpQuestion4, waterRetention: value})} 
                  className="flex flex-row gap-6"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="yes" id="water-retention-yes" />
                    <Label htmlFor="water-retention-yes" className="cursor-pointer">Yes</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="no" id="water-retention-no" />
                    <Label htmlFor="water-retention-no" className="cursor-pointer">No</Label>
                  </div>
                </RadioGroup>
              </div>

              {/* Increased dizziness */}
              <div className="flex items-center justify-between">
                <Label className="text-base font-medium">Increased dizziness</Label>
                <RadioGroup 
                  value={highBpQuestion4.dizziness} 
                  onValueChange={(value) => setHighBpQuestion4({...highBpQuestion4, dizziness: value})} 
                  className="flex flex-row gap-6"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="yes" id="dizziness-yes" />
                    <Label htmlFor="dizziness-yes" className="cursor-pointer">Yes</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="no" id="dizziness-no" />
                    <Label htmlFor="dizziness-no" className="cursor-pointer">No</Label>
                  </div>
                </RadioGroup>
              </div>

              {/* Increased fatigue */}
              <div className="flex items-center justify-between">
                <Label className="text-base font-medium">Increased fatigue</Label>
                <RadioGroup 
                  value={highBpQuestion4.fatigue} 
                  onValueChange={(value) => setHighBpQuestion4({...highBpQuestion4, fatigue: value})} 
                  className="flex flex-row gap-6"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="yes" id="fatigue-yes" />
                    <Label htmlFor="fatigue-yes" className="cursor-pointer">Yes</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="no" id="fatigue-no" />
                    <Label htmlFor="fatigue-no" className="cursor-pointer">No</Label>
                  </div>
                </RadioGroup>
              </div>

              {/* Severe Headache */}
              <div className="flex items-center justify-between">
                <Label className="text-base font-medium">Severe Headache</Label>
                <RadioGroup 
                  value={highBpQuestion4.severeHeadache} 
                  onValueChange={(value) => setHighBpQuestion4({...highBpQuestion4, severeHeadache: value})} 
                  className="flex flex-row gap-6"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="yes" id="severe-headache-yes" />
                    <Label htmlFor="severe-headache-yes" className="cursor-pointer">Yes</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="no" id="severe-headache-no" />
                    <Label htmlFor="severe-headache-no" className="cursor-pointer">No</Label>
                  </div>
                </RadioGroup>
              </div>

              {/* Blurred vision (new onset) */}
              <div className="flex items-center justify-between">
                <Label className="text-base font-medium">Blurred vision (new onset)</Label>
                <RadioGroup 
                  value={highBpQuestion4.blurredVision} 
                  onValueChange={(value) => setHighBpQuestion4({...highBpQuestion4, blurredVision: value})} 
                  className="flex flex-row gap-6"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="yes" id="blurred-vision-yes" />
                    <Label htmlFor="blurred-vision-yes" className="cursor-pointer">Yes</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="no" id="blurred-vision-no" />
                    <Label htmlFor="blurred-vision-no" className="cursor-pointer">No</Label>
                  </div>
                </RadioGroup>
              </div>

              {/* Increased Confusion */}
              <div className="flex items-center justify-between">
                <Label className="text-base font-medium">Increased Confusion</Label>
                <RadioGroup 
                  value={highBpQuestion4.confusion} 
                  onValueChange={(value) => setHighBpQuestion4({...highBpQuestion4, confusion: value})} 
                  className="flex flex-row gap-6"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="yes" id="confusion-high-yes" />
                    <Label htmlFor="confusion-high-yes" className="cursor-pointer">Yes</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="no" id="confusion-high-no" />
                    <Label htmlFor="confusion-high-no" className="cursor-pointer">No</Label>
                  </div>
                </RadioGroup>
              </div>

              {/* Severe Anxiety */}
              <div className="flex items-center justify-between">
                <Label className="text-base font-medium">Severe Anxiety</Label>
                <RadioGroup 
                  value={highBpQuestion4.severeAnxiety} 
                  onValueChange={(value) => setHighBpQuestion4({...highBpQuestion4, severeAnxiety: value})} 
                  className="flex flex-row gap-6"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="yes" id="severe-anxiety-yes" />
                    <Label htmlFor="severe-anxiety-yes" className="cursor-pointer">Yes</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="no" id="severe-anxiety-no" />
                    <Label htmlFor="severe-anxiety-no" className="cursor-pointer">No</Label>
                  </div>
                </RadioGroup>
              </div>

              {/* Seizure */}
              <div className="flex items-center justify-between">
                <Label className="text-base font-medium">Seizure</Label>
                <RadioGroup 
                  value={highBpQuestion4.seizure} 
                  onValueChange={(value) => setHighBpQuestion4({...highBpQuestion4, seizure: value})} 
                  className="flex flex-row gap-6"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="yes" id="seizure-yes" />
                    <Label htmlFor="seizure-yes" className="cursor-pointer">Yes</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="no" id="seizure-no" />
                    <Label htmlFor="seizure-no" className="cursor-pointer">No</Label>
                  </div>
                </RadioGroup>
              </div>

              {/* Unresponsiveness */}
              <div className="flex items-center justify-between">
                <Label className="text-base font-medium">Unresponsiveness</Label>
                <RadioGroup 
                  value={highBpQuestion4.unresponsiveness} 
                  onValueChange={(value) => setHighBpQuestion4({...highBpQuestion4, unresponsiveness: value})} 
                  className="flex flex-row gap-6"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="yes" id="unresponsiveness-yes" />
                    <Label htmlFor="unresponsiveness-yes" className="cursor-pointer">Yes</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="no" id="unresponsiveness-no" />
                    <Label htmlFor="unresponsiveness-no" className="cursor-pointer">No</Label>
                  </div>
                </RadioGroup>
              </div>

              {/* No symptoms checkbox */}
              <div className="flex items-center space-x-2 pt-2">
                <Checkbox 
                  id="no-high-symptoms" 
                  checked={noHighSymptoms}
                  onCheckedChange={(checked) => setNoHighSymptoms(checked === true)}
                />
                <Label htmlFor="no-high-symptoms" className="cursor-pointer text-base font-medium">
                  The patient is not experiencing any signs/symptoms of a hypertensive crisis
                </Label>
              </div>

              <div className="flex justify-between gap-3 pt-4">
                <Button variant="outline" onClick={handleBack} className="flex items-center gap-2">
                  <ArrowLeft className="h-4 w-4" />
                  Back
                </Button>
                <div className="flex gap-3">
                  <Button variant="outline" onClick={() => navigate("/alerts")}>Cancel</Button>
                  <Button 
                    onClick={handleHighBpQuestion4Confirm} 
                    className={primaryBtnClass}
                    disabled={btnLoading}
                  >
                    {btnLoading ? "Submitting..." : "Submit"}
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* High BP Question 5 (Recommendations - for "No" case) */}
      {currentStep === "high_q5" && (
        <Card>
          <CardHeader>
            <CardTitle>Based on this assessment, I recommend the following: (select all that apply)</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {/* Improve diet */}
              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="improve-diet" 
                  checked={highBpRecommendations.improveDiet}
                  onCheckedChange={(checked) => setHighBpRecommendations({...highBpRecommendations, improveDiet: checked === true})}
                />
                <Label htmlFor="improve-diet" className="cursor-pointer text-base">Improve diet</Label>
              </div>

              {/* Decrease salt/sodium intake */}
              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="decrease-salt" 
                  checked={highBpRecommendations.decreaseSalt}
                  onCheckedChange={(checked) => setHighBpRecommendations({...highBpRecommendations, decreaseSalt: checked === true})}
                />
                <Label htmlFor="decrease-salt" className="cursor-pointer text-base">Decrease salt/sodium intake</Label>
              </div>

              {/* Daily exercise */}
              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="daily-exercise" 
                  checked={highBpRecommendations.dailyExercise}
                  onCheckedChange={(checked) => setHighBpRecommendations({...highBpRecommendations, dailyExercise: checked === true})}
                />
                <Label htmlFor="daily-exercise" className="cursor-pointer text-base">Daily exercise</Label>
              </div>

              {/* Weight loss */}
              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="weight-loss" 
                  checked={highBpRecommendations.weightLoss}
                  onCheckedChange={(checked) => setHighBpRecommendations({...highBpRecommendations, weightLoss: checked === true})}
                />
                <Label htmlFor="weight-loss" className="cursor-pointer text-base">Weight loss</Label>
              </div>

              {/* Reduce caffeine */}
              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="reduce-caffeine" 
                  checked={highBpRecommendations.reduceCaffeine}
                  onCheckedChange={(checked) => setHighBpRecommendations({...highBpRecommendations, reduceCaffeine: checked === true})}
                />
                <Label htmlFor="reduce-caffeine" className="cursor-pointer text-base">Reduce caffine</Label>
              </div>

              {/* Reduce alcohol */}
              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="reduce-alcohol" 
                  checked={highBpRecommendations.reduceAlcohol}
                  onCheckedChange={(checked) => setHighBpRecommendations({...highBpRecommendations, reduceAlcohol: checked === true})}
                />
                <Label htmlFor="reduce-alcohol" className="cursor-pointer text-base">Reduce alcohol</Label>
              </div>

              {/* Reduce smoking */}
              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="reduce-smoking" 
                  checked={highBpRecommendations.reduceSmoking}
                  onCheckedChange={(checked) => setHighBpRecommendations({...highBpRecommendations, reduceSmoking: checked === true})}
                />
                <Label htmlFor="reduce-smoking" className="cursor-pointer text-base">Reduce smoking</Label>
              </div>

              {/* Reduce stress */}
              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="reduce-stress" 
                  checked={highBpRecommendations.reduceStress}
                  onCheckedChange={(checked) => setHighBpRecommendations({...highBpRecommendations, reduceStress: checked === true})}
                />
                <Label htmlFor="reduce-stress" className="cursor-pointer text-base">Reduce stress</Label>
              </div>

              {/* Take blood pressure medication as prescribed */}
              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="take-medication" 
                  checked={highBpRecommendations.takeMedication}
                  onCheckedChange={(checked) => setHighBpRecommendations({...highBpRecommendations, takeMedication: checked === true})}
                />
                <Label htmlFor="take-medication" className="cursor-pointer text-base">Take blood pressure medication as prescribed</Label>
              </div>

              {/* Monitor blood pressure daily */}
              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="monitor-bp-daily" 
                  checked={highBpRecommendations.monitorBpDaily}
                  onCheckedChange={(checked) => setHighBpRecommendations({...highBpRecommendations, monitorBpDaily: checked === true})}
                />
                <Label htmlFor="monitor-bp-daily" className="cursor-pointer text-base">Monitor blood pressure daily</Label>
              </div>

              {/* Escalate to provider */}
              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="escalate-provider" 
                  checked={highBpRecommendations.escalateToProvider}
                  onCheckedChange={(checked) => setHighBpRecommendations({...highBpRecommendations, escalateToProvider: checked === true})}
                />
                <Label htmlFor="escalate-provider" className="cursor-pointer text-base">Escalate to provider</Label>
              </div>

              {/* Take your blood pressure reading first thing in the morning... */}
              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="take-bp-morning" 
                  checked={highBpRecommendations.takeBpMorning}
                  onCheckedChange={(checked) => setHighBpRecommendations({...highBpRecommendations, takeBpMorning: checked === true})}
                />
                <Label htmlFor="take-bp-morning" className="cursor-pointer text-base">
                  Take your blood pressure reading first thing in the morning, before taking any blood pressure medications. Re-check your blood pressure again 2 hours after taking your blood pressure medication.
                </Label>
              </div>

              <div className="flex justify-between gap-3 pt-4">
                <Button variant="outline" onClick={handleBack} className="flex items-center gap-2">
                  <ArrowLeft className="h-4 w-4" />
                  Back
                </Button>
                <div className="flex gap-3">
                  <Button variant="outline" onClick={() => navigate("/alerts")}>Cancel</Button>
                  <Button 
                    onClick={handleHighBpRecommendationsConfirm} 
                    className={primaryBtnClass}
                    disabled={btnLoading}
                  >
                    {btnLoading ? "Submitting..." : "Submit"}
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Low BP Question 1 */}
      {currentStep === "low_question1" && (
        <Card>
          <CardHeader>
            <CardTitle>Is blood pressure &lt;90 systolically and/or &lt;60 diastolically ?</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <RadioGroup value={lowBpQuestion1} onValueChange={setLowBpQuestion1} className="flex flex-row gap-6">
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="yes" id="low-bp-q1-yes" />
                  <Label htmlFor="low-bp-q1-yes" className="cursor-pointer">Yes</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="no" id="low-bp-q1-no" />
                  <Label htmlFor="low-bp-q1-no" className="cursor-pointer">No</Label>
                </div>
              </RadioGroup>
              
              {/* Show warning when Yes is selected */}
              {lowBpQuestion1 === "yes" && (
                <div className="bg-red-500 text-white p-3 rounded-md">
                  <p className="text-sm font-medium">Note to Admin:</p>
                  <p className="text-sm mt-1">This patient may be experiencing a Hypotensive Crisis which can be a serious, life-threatening condition.</p>
                </div>
              )}
              
              <div className="flex justify-between gap-3 pt-4">
                <Button variant="outline" onClick={handleBack} className="flex items-center gap-2">
                  <ArrowLeft className="h-4 w-4" />
                  Back
                </Button>
                <div className="flex gap-3">
                  <Button variant="outline" onClick={() => navigate("/alerts")}>
                    Cancel
                  </Button>
                  <Button onClick={handleLowBpQuestion1Confirm} className={primaryBtnClass}>
                    Confirm
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Low BP Question 2 (after Yes on low_question1) */}
      {currentStep === "low_high_q2" && (
        <Card>
          <CardHeader>
            <CardTitle>Have you ever experienced a hypotensive crisis in the past?</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <RadioGroup value={lowBpHighQuestion2} onValueChange={setLowBpHighQuestion2} className="flex flex-row gap-6">
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="yes" id="low-high-q2-yes" />
                  <Label htmlFor="low-high-q2-yes" className="cursor-pointer">Yes</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="no" id="low-high-q2-no" />
                  <Label htmlFor="low-high-q2-no" className="cursor-pointer">No</Label>
                </div>
              </RadioGroup>
              {lowBpHighQuestion2 === "yes" && (
                <div className="space-y-2">
                  <Label htmlFor="low-high-q2-explanation">Please explain</Label>
                  <Textarea
                    id="low-high-q2-explanation"
                    placeholder="Please explain..."
                    value={lowBpHighQuestion2Explanation}
                    onChange={(e) => setLowBpHighQuestion2Explanation(e.target.value)}
                    className="min-h-[100px]"
                  />
                </div>
              )}
              <div className="flex justify-between gap-3 pt-4">
                <Button variant="outline" onClick={handleBack} className="flex items-center gap-2">
                  <ArrowLeft className="h-4 w-4" />
                  Back
                </Button>
                <div className="flex gap-3">
                  <Button variant="outline" onClick={() => navigate("/alerts")}>Cancel</Button>
                  <Button onClick={handleLowBpHighQuestion2Confirm} className={primaryBtnClass}>
                    Confirm
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Low BP Question 3 (after low_high_q2) */}
      {currentStep === "low_high_q3" && (
        <Card>
          <CardHeader>
            <CardTitle>How do you feel?</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="low-high-q3-feelings">Please describe how you feel:</Label>
                <Textarea
                  id="low-high-q3-feelings"
                  placeholder="Please describe how you feel..."
                  value={lowBpHighQuestion3}
                  onChange={(e) => setLowBpHighQuestion3(e.target.value)}
                  className="min-h-[120px]"
                />
              </div>
              <div className="flex justify-between gap-3 pt-4">
                <Button variant="outline" onClick={handleBack} className="flex items-center gap-2">
                  <ArrowLeft className="h-4 w-4" />
                  Back
                </Button>
                <div className="flex gap-3">
                  <Button variant="outline" onClick={() => navigate("/alerts")}>Cancel</Button>
                  <Button onClick={handleLowBpHighQuestion3Confirm} className={primaryBtnClass}>
                    Confirm
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Low BP Question 4 (Symptoms Question) */}
      {currentStep === "low_high_q4" && (
        <Card>
          <CardHeader>
            <CardTitle>Are you currently experiencing any of the following?</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-6">
              {/* A rapid pulse (>120 bpm) */}
              <div className="flex items-center justify-between">
                <Label className="text-base font-medium">A rapid pulse (&gt;120 bpm)</Label>
                <RadioGroup 
                  value={lowBpHighQuestion4.rapidPulse} 
                  onValueChange={(value) => setLowBpHighQuestion4({...lowBpHighQuestion4, rapidPulse: value})} 
                  className="flex flex-row gap-6"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="yes" id="rapid-pulse-yes" />
                    <Label htmlFor="rapid-pulse-yes" className="cursor-pointer">Yes</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="no" id="rapid-pulse-no" />
                    <Label htmlFor="rapid-pulse-no" className="cursor-pointer">No</Label>
                  </div>
                </RadioGroup>
              </div>

              {/* Shallow breathing */}
              <div className="flex items-center justify-between">
                <Label className="text-base font-medium">Shallow breathing</Label>
                <RadioGroup 
                  value={lowBpHighQuestion4.shallowBreathing} 
                  onValueChange={(value) => setLowBpHighQuestion4({...lowBpHighQuestion4, shallowBreathing: value})} 
                  className="flex flex-row gap-6"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="yes" id="shallow-breathing-yes" />
                    <Label htmlFor="shallow-breathing-yes" className="cursor-pointer">Yes</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="no" id="shallow-breathing-no" />
                    <Label htmlFor="shallow-breathing-no" className="cursor-pointer">No</Label>
                  </div>
                </RadioGroup>
              </div>

              {/* Cold or clammy skin */}
              <div className="flex items-center justify-between">
                <Label className="text-base font-medium">Cold or clammy skin</Label>
                <RadioGroup 
                  value={lowBpHighQuestion4.coldSkin} 
                  onValueChange={(value) => setLowBpHighQuestion4({...lowBpHighQuestion4, coldSkin: value})} 
                  className="flex flex-row gap-6"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="yes" id="cold-skin-yes" />
                    <Label htmlFor="cold-skin-yes" className="cursor-pointer">Yes</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="no" id="cold-skin-no" />
                    <Label htmlFor="cold-skin-no" className="cursor-pointer">No</Label>
                  </div>
                </RadioGroup>
              </div>

              {/* Fainting */}
              <div className="flex items-center justify-between">
                <Label className="text-base font-medium">Fainting</Label>
                <RadioGroup 
                  value={lowBpHighQuestion4.fainting} 
                  onValueChange={(value) => setLowBpHighQuestion4({...lowBpHighQuestion4, fainting: value})} 
                  className="flex flex-row gap-6"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="yes" id="fainting-yes" />
                    <Label htmlFor="fainting-yes" className="cursor-pointer">Yes</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="no" id="fainting-no" />
                    <Label htmlFor="fainting-no" className="cursor-pointer">No</Label>
                  </div>
                </RadioGroup>
              </div>

              {/* Confusion */}
              <div className="flex items-center justify-between">
                <Label className="text-base font-medium">Confusion</Label>
                <RadioGroup 
                  value={lowBpHighQuestion4.confusion} 
                  onValueChange={(value) => setLowBpHighQuestion4({...lowBpHighQuestion4, confusion: value})} 
                  className="flex flex-row gap-6"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="yes" id="confusion-yes" />
                    <Label htmlFor="confusion-yes" className="cursor-pointer">Yes</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="no" id="confusion-no" />
                    <Label htmlFor="confusion-no" className="cursor-pointer">No</Label>
                  </div>
                </RadioGroup>
              </div>

              {/* Bleeding */}
              <div className="flex items-center justify-between">
                <Label className="text-base font-medium">Bleeding</Label>
                <RadioGroup 
                  value={lowBpHighQuestion4.bleeding} 
                  onValueChange={(value) => setLowBpHighQuestion4({...lowBpHighQuestion4, bleeding: value})} 
                  className="flex flex-row gap-6"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="yes" id="bleeding-yes" />
                    <Label htmlFor="bleeding-yes" className="cursor-pointer">Yes</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="no" id="bleeding-no" />
                    <Label htmlFor="bleeding-no" className="cursor-pointer">No</Label>
                  </div>
                </RadioGroup>
              </div>

              {/* No symptoms checkbox */}
              <div className="flex items-center space-x-2 pt-2">
                <Checkbox 
                  id="no-symptoms" 
                  checked={noSymptoms}
                  onCheckedChange={(checked) => setNoSymptoms(checked === true)}
                />
                <Label htmlFor="no-symptoms" className="cursor-pointer text-base font-medium">
                  The patient is not experiencing any signs/symptoms of a hypotensive crisis
                </Label>
              </div>

              <div className="flex justify-between gap-3 pt-4">
                <Button variant="outline" onClick={handleBack} className="flex items-center gap-2">
                  <ArrowLeft className="h-4 w-4" />
                  Back
                </Button>
                <div className="flex gap-3">
                  <Button variant="outline" onClick={() => navigate("/alerts")}>Cancel</Button>
                  <Button 
                    onClick={handleLowBpHighQuestion4Confirm} 
                    className={primaryBtnClass}
                    disabled={btnLoading}
                  >
                    {btnLoading ? "Submitting..." : "Submit"}
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Low BP Recommendations Question (for "No" case) */}
      {currentStep === "low_high_q5" && (
        <Card>
          <CardHeader>
            <CardTitle>Based on this assessment, I recommend the following: (select all that apply)</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {/* Increase fluid intake */}
              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="increase-fluid-intake" 
                  checked={lowBpRecommendations.increaseFluidIntake}
                  onCheckedChange={(checked) => setLowBpRecommendations({...lowBpRecommendations, increaseFluidIntake: checked === true})}
                />
                <Label htmlFor="increase-fluid-intake" className="cursor-pointer text-base">Increase fluid intake</Label>
              </div>

              {/* Drink 8oz - 12oz of water */}
              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="drink-water" 
                  checked={lowBpRecommendations.drinkWater}
                  onCheckedChange={(checked) => setLowBpRecommendations({...lowBpRecommendations, drinkWater: checked === true})}
                />
                <Label htmlFor="drink-water" className="cursor-pointer text-base">Drink 8oz - 12oz of water</Label>
              </div>

              {/* Stand up slowly to avoid fainting */}
              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="stand-up-slowly" 
                  checked={lowBpRecommendations.standUpSlowly}
                  onCheckedChange={(checked) => setLowBpRecommendations({...lowBpRecommendations, standUpSlowly: checked === true})}
                />
                <Label htmlFor="stand-up-slowly" className="cursor-pointer text-base">Stand up slowly to avoid fainting</Label>
              </div>

              {/* Elevate feet */}
              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="elevate-feet" 
                  checked={lowBpRecommendations.elevateFeet}
                  onCheckedChange={(checked) => setLowBpRecommendations({...lowBpRecommendations, elevateFeet: checked === true})}
                />
                <Label htmlFor="elevate-feet" className="cursor-pointer text-base">Elevate feet</Label>
              </div>

              {/* Hold any blood pressure lowering medication */}
              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="hold-medication" 
                  checked={lowBpRecommendations.holdMedication}
                  onCheckedChange={(checked) => setLowBpRecommendations({...lowBpRecommendations, holdMedication: checked === true})}
                />
                <Label htmlFor="hold-medication" className="cursor-pointer text-base">Hold any blood pressure lowering medication</Label>
              </div>

              {/* Avoid caffeine */}
              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="avoid-caffeine-low" 
                  checked={lowBpRecommendations.avoidCaffeine}
                  onCheckedChange={(checked) => setLowBpRecommendations({...lowBpRecommendations, avoidCaffeine: checked === true})}
                />
                <Label htmlFor="avoid-caffeine-low" className="cursor-pointer text-base">Avoid caffeine</Label>
              </div>

              {/* Avoid alcohol */}
              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="avoid-alcohol-low" 
                  checked={lowBpRecommendations.avoidAlcohol}
                  onCheckedChange={(checked) => setLowBpRecommendations({...lowBpRecommendations, avoidAlcohol: checked === true})}
                />
                <Label htmlFor="avoid-alcohol-low" className="cursor-pointer text-base">Avoid alcohol</Label>
              </div>

              {/* Avoid smoking */}
              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="avoid-smoking-low" 
                  checked={lowBpRecommendations.avoidSmoking}
                  onCheckedChange={(checked) => setLowBpRecommendations({...lowBpRecommendations, avoidSmoking: checked === true})}
                />
                <Label htmlFor="avoid-smoking-low" className="cursor-pointer text-base">Avoid smoking</Label>
              </div>

              {/* Reduce stress */}
              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="reduce-stress-low" 
                  checked={lowBpRecommendations.reduceStress}
                  onCheckedChange={(checked) => setLowBpRecommendations({...lowBpRecommendations, reduceStress: checked === true})}
                />
                <Label htmlFor="reduce-stress-low" className="cursor-pointer text-base">Reduce stress</Label>
              </div>

              {/* Take blood pressure medication as prescribed */}
              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="take-medication-low" 
                  checked={lowBpRecommendations.takeMedication}
                  onCheckedChange={(checked) => setLowBpRecommendations({...lowBpRecommendations, takeMedication: checked === true})}
                />
                <Label htmlFor="take-medication-low" className="cursor-pointer text-base">Take blood pressure medication as prescribed</Label>
              </div>

              {/* Monitor blood pressure daily */}
              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="monitor-bp-daily-low" 
                  checked={lowBpRecommendations.monitorBpDaily}
                  onCheckedChange={(checked) => setLowBpRecommendations({...lowBpRecommendations, monitorBpDaily: checked === true})}
                />
                <Label htmlFor="monitor-bp-daily-low" className="cursor-pointer text-base">Monitor blood pressure daily</Label>
              </div>

              {/* Escalate to provider */}
              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="escalate-provider-low" 
                  checked={lowBpRecommendations.escalateToProvider}
                  onCheckedChange={(checked) => setLowBpRecommendations({...lowBpRecommendations, escalateToProvider: checked === true})}
                />
                <Label htmlFor="escalate-provider-low" className="cursor-pointer text-base">Escalate to provider</Label>
              </div>

              {/* Take your blood pressure reading first thing in the morning... */}
              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="take-bp-morning-low" 
                  checked={lowBpRecommendations.takeBpMorning}
                  onCheckedChange={(checked) => setLowBpRecommendations({...lowBpRecommendations, takeBpMorning: checked === true})}
                />
                <Label htmlFor="take-bp-morning-low" className="cursor-pointer text-base">
                  Take your blood pressure reading first thing in the morning, before taking any blood pressure medications. Re-check your blood pressure again 2 hours after taking your blood pressure medication.
                </Label>
              </div>

              <div className="flex justify-between gap-3 pt-4">
                <Button variant="outline" onClick={handleBack} className="flex items-center gap-2">
                  <ArrowLeft className="h-4 w-4" />
                  Back
                </Button>
                <div className="flex gap-3">
                  <Button variant="outline" onClick={() => navigate("/alerts")}>Cancel</Button>
                  <Button 
                    onClick={handleLowBpRecommendationsConfirm} 
                    className={primaryBtnClass}
                    disabled={btnLoading}
                  >
                    {btnLoading ? "Submitting..." : "Submit"}
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Low BP Follow-up Questions */}
      {currentStep === "low_q2" && (
        <Card>
          <CardHeader>
            <CardTitle>Is there anything unusual in your daily routine today that could explain your low blood pressure?</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <RadioGroup value={lowBpAnswers.low_q2 || ""} onValueChange={(value) => setLowBpAnswers({...lowBpAnswers, low_q2: value})} className="flex flex-row gap-6">
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="yes" id="low-q2-yes" />
                  <Label htmlFor="low-q2-yes" className="cursor-pointer">Yes</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="no" id="low-q2-no" />
                  <Label htmlFor="low-q2-no" className="cursor-pointer">No</Label>
                </div>
              </RadioGroup>
              {lowBpAnswers.low_q2 === "yes" && (
                <div className="space-y-2">
                  <Label htmlFor="low-q2-explanation">Please Explain</Label>
                  <Textarea
                    id="low-q2-explanation"
                    placeholder="Please Explain..."
                    value={lowBpAnswersExplanations.low_q2 || ""}
                    onChange={(e) => setLowBpAnswersExplanations({...lowBpAnswersExplanations, low_q2: e.target.value})}
                    className="min-h-[100px]"
                  />
                </div>
              )}
              <div className="flex justify-between gap-3 pt-4">
                <Button variant="outline" onClick={handleBack} className="flex items-center gap-2">
                  <ArrowLeft className="h-4 w-4" />
                  Back
                </Button>
                <div className="flex gap-3">
                  <Button variant="outline" onClick={() => navigate("/alerts")}>Cancel</Button>
                  <Button onClick={() => handleLowBpQuestion("low_q2", "low_q3")} className={primaryBtnClass}>
                    Confirm
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {currentStep === "low_q3" && (
        <Card>
          <CardHeader>
            <CardTitle>Have you taken any blood pressure lowering medication today (diuretics, High blood pressure medication, antidepressants, narcotics)?</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <RadioGroup value={lowBpAnswers.low_q3 || ""} onValueChange={(value) => setLowBpAnswers({...lowBpAnswers, low_q3: value})} className="flex flex-row gap-6">
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="yes" id="low-q3-yes" />
                  <Label htmlFor="low-q3-yes" className="cursor-pointer">Yes</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="no" id="low-q3-no" />
                  <Label htmlFor="low-q3-no" className="cursor-pointer">No</Label>
                </div>
              </RadioGroup>
              {lowBpAnswers.low_q3 === "yes" && (
                <div className="space-y-2">
                  <Label htmlFor="low-q3-explanation">Please enter medications taken today, time taken, dosage taken</Label>
                  <Textarea
                    id="low-q3-explanation"
                    placeholder="Please enter medications taken today, time taken, dosage taken..."
                    value={lowBpAnswersExplanations.low_q3 || ""}
                    onChange={(e) => setLowBpAnswersExplanations({...lowBpAnswersExplanations, low_q3: e.target.value})}
                    className="min-h-[100px]"
                  />
                </div>
              )}
              <div className="flex justify-between gap-3 pt-4">
                <Button variant="outline" onClick={handleBack} className="flex items-center gap-2">
                  <ArrowLeft className="h-4 w-4" />
                  Back
                </Button>
                <div className="flex gap-3">
                  <Button variant="outline" onClick={() => navigate("/alerts")}>Cancel</Button>
                  <Button onClick={() => handleLowBpQuestion("low_q3", "low_q4")} className={primaryBtnClass}>
                    Confirm
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {currentStep === "low_q4" && (
        <Card>
          <CardHeader>
            <CardTitle>Have you been drinking enough fluid?</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <RadioGroup value={lowBpAnswers.low_q4 || ""} onValueChange={(value) => setLowBpAnswers({...lowBpAnswers, low_q4: value})} className="flex flex-row gap-6">
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="yes" id="low-q4-yes" />
                  <Label htmlFor="low-q4-yes" className="cursor-pointer">Yes</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="no" id="low-q4-no" />
                  <Label htmlFor="low-q4-no" className="cursor-pointer">No</Label>
                </div>
              </RadioGroup>
              {lowBpAnswers.low_q4 === "no" && (
                <div className="space-y-2">
                  <Label htmlFor="low-q4-explanation">Please Explain</Label>
                  <Textarea
                    id="low-q4-explanation"
                    placeholder="Please Explain..."
                    value={lowBpAnswersExplanations.low_q4 || ""}
                    onChange={(e) => setLowBpAnswersExplanations({...lowBpAnswersExplanations, low_q4: e.target.value})}
                    className="min-h-[100px]"
                  />
                </div>
              )}
              <div className="flex justify-between gap-3 pt-4">
                <Button variant="outline" onClick={handleBack} className="flex items-center gap-2">
                  <ArrowLeft className="h-4 w-4" />
                  Back
                </Button>
                <div className="flex gap-3">
                  <Button variant="outline" onClick={() => navigate("/alerts")}>Cancel</Button>
                  <Button onClick={() => handleLowBpQuestion("low_q4", "low_q5")} className={primaryBtnClass}>
                    Confirm
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {currentStep === "low_q5" && (
        <Card>
          <CardHeader>
            <CardTitle>Could you be dehydrated?</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <RadioGroup value={lowBpAnswers.low_q5 || ""} onValueChange={(value) => setLowBpAnswers({...lowBpAnswers, low_q5: value})} className="flex flex-row gap-6">
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="yes" id="low-q5-yes" />
                  <Label htmlFor="low-q5-yes" className="cursor-pointer">Yes</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="no" id="low-q5-no" />
                  <Label htmlFor="low-q5-no" className="cursor-pointer">No</Label>
                </div>
              </RadioGroup>
              {lowBpAnswers.low_q5 === "yes" && (
                <div className="space-y-2">
                  <Label htmlFor="low-q5-explanation">Please Explain</Label>
                  <Textarea
                    id="low-q5-explanation"
                    placeholder="Please Explain..."
                    value={lowBpAnswersExplanations.low_q5 || ""}
                    onChange={(e) => setLowBpAnswersExplanations({...lowBpAnswersExplanations, low_q5: e.target.value})}
                    className="min-h-[100px]"
                  />
                </div>
              )}
              <div className="flex justify-between gap-3 pt-4">
                <Button variant="outline" onClick={handleBack} className="flex items-center gap-2">
                  <ArrowLeft className="h-4 w-4" />
                  Back
                </Button>
                <div className="flex gap-3">
                  <Button variant="outline" onClick={() => navigate("/alerts")}>Cancel</Button>
                  <Button onClick={() => handleLowBpQuestion("low_q5", "low_q6")} className={primaryBtnClass}>
                    Confirm
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {currentStep === "low_q6" && (
        <Card>
          <CardHeader>
            <CardTitle>Are you on any new medications, including prescription, over-the-counter, or illicit drugs (diuretics, decongestants, steroids, stimulants)?</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <RadioGroup value={lowBpAnswers.low_q6 || ""} onValueChange={(value) => setLowBpAnswers({...lowBpAnswers, low_q6: value})} className="flex flex-row gap-6">
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="yes" id="low-q6-yes" />
                  <Label htmlFor="low-q6-yes" className="cursor-pointer">Yes</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="no" id="low-q6-no" />
                  <Label htmlFor="low-q6-no" className="cursor-pointer">No</Label>
                </div>
              </RadioGroup>
              {lowBpAnswers.low_q6 === "yes" && (
                <div className="space-y-2">
                  <Label htmlFor="low-q6-explanation">Please Explain</Label>
                  <Textarea
                    id="low-q6-explanation"
                    placeholder="Please Explain..."
                    value={lowBpAnswersExplanations.low_q6 || ""}
                    onChange={(e) => setLowBpAnswersExplanations({...lowBpAnswersExplanations, low_q6: e.target.value})}
                    className="min-h-[100px]"
                  />
                </div>
              )}
              <div className="flex justify-between gap-3 pt-4">
                <Button variant="outline" onClick={handleBack} className="flex items-center gap-2">
                  <ArrowLeft className="h-4 w-4" />
                  Back
                </Button>
                <div className="flex gap-3">
                  <Button variant="outline" onClick={() => navigate("/alerts")}>Cancel</Button>
                  <Button onClick={() => handleLowBpQuestion("low_q6", "low_q7")} className={primaryBtnClass}>
                    Confirm
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {currentStep === "low_q7" && (
        <Card>
          <CardHeader>
            <CardTitle>Have you consumed any alcohol?</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <RadioGroup value={lowBpAnswers.low_q7 || ""} onValueChange={(value) => setLowBpAnswers({...lowBpAnswers, low_q7: value})} className="flex flex-row gap-6">
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="yes" id="low-q7-yes" />
                  <Label htmlFor="low-q7-yes" className="cursor-pointer">Yes</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="no" id="low-q7-no" />
                  <Label htmlFor="low-q7-no" className="cursor-pointer">No</Label>
                </div>
              </RadioGroup>
              {lowBpAnswers.low_q7 === "yes" && (
                <div className="space-y-2">
                  <Label htmlFor="low-q7-explanation">How much alcohol have you consumed in the last 48 hours?</Label>
                  <Textarea
                    id="low-q7-explanation"
                    placeholder="Please provide details..."
                    value={lowBpAnswersExplanations.low_q7 || ""}
                    onChange={(e) => setLowBpAnswersExplanations({...lowBpAnswersExplanations, low_q7: e.target.value})}
                    className="min-h-[100px]"
                  />
                </div>
              )}
              <div className="flex justify-between gap-3 pt-4">
                <Button variant="outline" onClick={handleBack} className="flex items-center gap-2">
                  <ArrowLeft className="h-4 w-4" />
                  Back
                </Button>
                <div className="flex gap-3">
                  <Button variant="outline" onClick={() => navigate("/alerts")}>Cancel</Button>
                  <Button onClick={() => handleLowBpQuestion("low_q7", "low_q8")} className={primaryBtnClass}>
                    Confirm
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {currentStep === "low_q8" && (
        <Card>
          <CardHeader>
            <CardTitle>Have you been smoking?</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <RadioGroup value={lowBpAnswers.low_q8 || ""} onValueChange={(value) => setLowBpAnswers({...lowBpAnswers, low_q8: value})} className="flex flex-row gap-6">
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="yes" id="low-q8-yes" />
                  <Label htmlFor="low-q8-yes" className="cursor-pointer">Yes</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="no" id="low-q8-no" />
                  <Label htmlFor="low-q8-no" className="cursor-pointer">No</Label>
                </div>
              </RadioGroup>
              {lowBpAnswers.low_q8 === "yes" && (
                <div className="space-y-2">
                  <Label htmlFor="low-q8-explanation">How much more than usual have you been smoking? What have you been smoking?</Label>
                  <Textarea
                    id="low-q8-explanation"
                    placeholder="Please provide details..."
                    value={lowBpAnswersExplanations.low_q8 || ""}
                    onChange={(e) => setLowBpAnswersExplanations({...lowBpAnswersExplanations, low_q8: e.target.value})}
                    className="min-h-[100px]"
                  />
                </div>
              )}
              <div className="flex justify-between gap-3 pt-4">
                <Button variant="outline" onClick={handleBack} className="flex items-center gap-2">
                  <ArrowLeft className="h-4 w-4" />
                  Back
                </Button>
                <div className="flex gap-3">
                  <Button variant="outline" onClick={() => navigate("/alerts")}>Cancel</Button>
                  <Button onClick={() => handleLowBpQuestion("low_q8", "low_q9")} className={primaryBtnClass}>
                    Confirm
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {currentStep === "low_q9" && (
        <Card>
          <CardHeader>
            <CardTitle>Have you used an anti-inflammatory drug, such as ibuprofen?</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <RadioGroup value={lowBpAnswers.low_q9 || ""} onValueChange={(value) => setLowBpAnswers({...lowBpAnswers, low_q9: value})} className="flex flex-row gap-6">
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="yes" id="low-q9-yes" />
                  <Label htmlFor="low-q9-yes" className="cursor-pointer">Yes</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="no" id="low-q9-no" />
                  <Label htmlFor="low-q9-no" className="cursor-pointer">No</Label>
                </div>
              </RadioGroup>
              {lowBpAnswers.low_q9 === "yes" && (
                <div className="space-y-2">
                  <Label htmlFor="low-q9-explanation">What have you been taking? How much have you been taking?</Label>
                  <Textarea
                    id="low-q9-explanation"
                    placeholder="What have you been taking? How much have you been taking?..."
                    value={lowBpAnswersExplanations.low_q9 || ""}
                    onChange={(e) => setLowBpAnswersExplanations({...lowBpAnswersExplanations, low_q9: e.target.value})}
                    className="min-h-[100px]"
                  />
                </div>
              )}
              <div className="flex justify-between gap-3 pt-4">
                <Button variant="outline" onClick={handleBack} className="flex items-center gap-2">
                  <ArrowLeft className="h-4 w-4" />
                  Back
                </Button>
                <div className="flex gap-3">
                  <Button variant="outline" onClick={() => navigate("/alerts")}>Cancel</Button>
                  <Button onClick={() => handleLowBpQuestion("low_q9", "low_q10")} className={primaryBtnClass}>
                    Confirm
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {currentStep === "low_q10" && (
        <Card>
          <CardHeader>
            <CardTitle>(If not using a connected scale) Have you lost weight recently?</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <RadioGroup value={lowBpAnswers.low_q10 || ""} onValueChange={(value) => setLowBpAnswers({...lowBpAnswers, low_q10: value})} className="flex flex-row gap-6">
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="yes" id="low-q10-yes" />
                  <Label htmlFor="low-q10-yes" className="cursor-pointer">Yes</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="no" id="low-q10-no" />
                  <Label htmlFor="low-q10-no" className="cursor-pointer">No</Label>
                </div>
              </RadioGroup>
              {lowBpAnswers.low_q10 === "yes" && (
                <div className="space-y-2">
                  <Label htmlFor="low-q10-explanation">How much weight have you lost?</Label>
                  <Textarea
                    id="low-q10-explanation"
                    placeholder="How much weight have you lost?..."
                    value={lowBpAnswersExplanations.low_q10 || ""}
                    onChange={(e) => setLowBpAnswersExplanations({...lowBpAnswersExplanations, low_q10: e.target.value})}
                    className="min-h-[100px]"
                  />
                </div>
              )}
              <div className="flex justify-between gap-3 pt-4">
                <Button variant="outline" onClick={handleBack} className="flex items-center gap-2">
                  <ArrowLeft className="h-4 w-4" />
                  Back
                </Button>
                <div className="flex gap-3">
                  <Button variant="outline" onClick={() => navigate("/alerts")}>Cancel</Button>
                  <Button onClick={() => handleLowBpQuestion("low_q10", "low_q11")} className={primaryBtnClass}>
                    Confirm
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {currentStep === "low_q11" && (
        <Card>
          <CardHeader>
            <CardTitle>How do you feel?</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="low-q11-feelings">Please describe how you feel:</Label>
                <Textarea
                  id="low-q11-feelings"
                  placeholder="Please describe how you feel..."
                  value={lowBpAnswers.low_q11 || ""}
                  onChange={(e) => setLowBpAnswers({...lowBpAnswers, low_q11: e.target.value})}
                  className="min-h-[120px]"
                />
              </div>
              <div className="flex justify-between gap-3 pt-4">
                <Button variant="outline" onClick={handleBack} className="flex items-center gap-2">
                  <ArrowLeft className="h-4 w-4" />
                  Back
                </Button>
                <div className="flex gap-3">
                  <Button variant="outline" onClick={() => navigate("/alerts")}>Cancel</Button>
                  <Button 
                    onClick={() => handleLowBpQuestion("low_q11", "complete")} 
                    className={primaryBtnClass}
                  >
                    Confirm
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* High BP Follow-up Questions (when answer is "no" to >180/>110) */}
      {currentStep === "high_followup_q1" && (
        <Card>
          <CardHeader>
            <CardTitle>Is there anything unusual in your daily routine today that could explain your elevated blood pressure?</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <RadioGroup value={highBpFollowupAnswers.high_followup_q1 || ""} onValueChange={(value) => setHighBpFollowupAnswers({...highBpFollowupAnswers, high_followup_q1: value})} className="flex flex-row gap-6">
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="yes" id="high-followup-q1-yes" />
                  <Label htmlFor="high-followup-q1-yes" className="cursor-pointer">Yes</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="no" id="high-followup-q1-no" />
                  <Label htmlFor="high-followup-q1-no" className="cursor-pointer">No</Label>
                </div>
              </RadioGroup>
              {highBpFollowupAnswers.high_followup_q1 === "yes" && (
                <div className="space-y-2">
                  <Label htmlFor="high-followup-q1-explanation">Please enter BP meds taken today, time taken, dosage taken</Label>
                  <Textarea
                    id="high-followup-q1-explanation"
                    placeholder="Please enter BP meds taken today, time taken, dosage taken..."
                    value={highBpFollowupExplanations.high_followup_q1 || ""}
                    onChange={(e) => setHighBpFollowupExplanations({...highBpFollowupExplanations, high_followup_q1: e.target.value})}
                    className="min-h-[100px]"
                  />
                </div>
              )}
              <div className="flex justify-between gap-3 pt-4">
                <Button variant="outline" onClick={handleBack} className="flex items-center gap-2">
                  <ArrowLeft className="h-4 w-4" />
                  Back
                </Button>
                <div className="flex gap-3">
                  <Button variant="outline" onClick={() => navigate("/alerts")}>Cancel</Button>
                  <Button onClick={() => handleHighBpFollowupQuestion("high_followup_q1", "high_followup_q2")} className={primaryBtnClass}>
                    Confirm
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {currentStep === "high_followup_q2" && (
        <Card>
          <CardHeader>
            <CardTitle>Did you take your blood pressure after activity?</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <RadioGroup value={highBpFollowupAnswers.high_followup_q2 || ""} onValueChange={(value) => setHighBpFollowupAnswers({...highBpFollowupAnswers, high_followup_q2: value})} className="flex flex-row gap-6">
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="yes" id="high-followup-q2-yes" />
                  <Label htmlFor="high-followup-q2-yes" className="cursor-pointer">Yes</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="no" id="high-followup-q2-no" />
                  <Label htmlFor="high-followup-q2-no" className="cursor-pointer">No</Label>
                </div>
              </RadioGroup>
              {highBpFollowupAnswers.high_followup_q2 === "yes" && (
                <div className="space-y-2">
                  <Label htmlFor="high-followup-q2-explanation">Which type of activity?</Label>
                  <Textarea
                    id="high-followup-q2-explanation"
                    placeholder="Which type of activity?..."
                    value={highBpFollowupExplanations.high_followup_q2 || ""}
                    onChange={(e) => setHighBpFollowupExplanations({...highBpFollowupExplanations, high_followup_q2: e.target.value})}
                    className="min-h-[100px]"
                  />
                </div>
              )}
              <div className="flex justify-between gap-3 pt-4">
                <Button variant="outline" onClick={handleBack} className="flex items-center gap-2">
                  <ArrowLeft className="h-4 w-4" />
                  Back
                </Button>
                <div className="flex gap-3">
                  <Button variant="outline" onClick={() => navigate("/alerts")}>Cancel</Button>
                  <Button onClick={() => handleHighBpFollowupQuestion("high_followup_q2", "high_followup_q3")} className={primaryBtnClass}>
                    Confirm
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {currentStep === "high_followup_q3" && (
        <Card>
          <CardHeader>
            <CardTitle>Are you feeling any stress, anxiety, or pain?</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <RadioGroup value={highBpFollowupAnswers.high_followup_q3 || ""} onValueChange={(value) => setHighBpFollowupAnswers({...highBpFollowupAnswers, high_followup_q3: value})} className="flex flex-row gap-6">
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="yes" id="high-followup-q3-yes" />
                  <Label htmlFor="high-followup-q3-yes" className="cursor-pointer">Yes</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="no" id="high-followup-q3-no" />
                  <Label htmlFor="high-followup-q3-no" className="cursor-pointer">No</Label>
                </div>
              </RadioGroup>
              {highBpFollowupAnswers.high_followup_q3 === "yes" && (
                <div className="space-y-2">
                  <Label htmlFor="high-followup-q3-explanation">Please Explain</Label>
                  <Textarea
                    id="high-followup-q3-explanation"
                    placeholder="Please Explain..."
                    value={highBpFollowupExplanations.high_followup_q3 || ""}
                    onChange={(e) => setHighBpFollowupExplanations({...highBpFollowupExplanations, high_followup_q3: e.target.value})}
                    className="min-h-[100px]"
                  />
                </div>
              )}
              <div className="flex justify-between gap-3 pt-4">
                <Button variant="outline" onClick={handleBack} className="flex items-center gap-2">
                  <ArrowLeft className="h-4 w-4" />
                  Back
                </Button>
                <div className="flex gap-3">
                  <Button variant="outline" onClick={() => navigate("/alerts")}>Cancel</Button>
                  <Button onClick={() => handleHighBpFollowupQuestion("high_followup_q3", "high_followup_q4")} className={primaryBtnClass}>
                    Confirm
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {currentStep === "high_followup_q4" && (
        <Card>
          <CardHeader>
            <CardTitle>Have you eaten more salty foods than usual (including adding salt to your food, restaurant food, frozen food, and canned food)?</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <RadioGroup value={highBpFollowupAnswers.high_followup_q4 || ""} onValueChange={(value) => setHighBpFollowupAnswers({...highBpFollowupAnswers, high_followup_q4: value})} className="flex flex-row gap-6">
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="yes" id="high-followup-q4-yes" />
                  <Label htmlFor="high-followup-q4-yes" className="cursor-pointer">Yes</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="no" id="high-followup-q4-no" />
                  <Label htmlFor="high-followup-q4-no" className="cursor-pointer">No</Label>
                </div>
              </RadioGroup>
              {highBpFollowupAnswers.high_followup_q4 === "yes" && (
                <div className="space-y-2">
                  <Label htmlFor="high-followup-q4-explanation">Please Explain</Label>
                  <Textarea
                    id="high-followup-q4-explanation"
                    placeholder="Please Explain..."
                    value={highBpFollowupExplanations.high_followup_q4 || ""}
                    onChange={(e) => setHighBpFollowupExplanations({...highBpFollowupExplanations, high_followup_q4: e.target.value})}
                    className="min-h-[100px]"
                  />
                </div>
              )}
              <div className="flex justify-between gap-3 pt-4">
                <Button variant="outline" onClick={handleBack} className="flex items-center gap-2">
                  <ArrowLeft className="h-4 w-4" />
                  Back
                </Button>
                <div className="flex gap-3">
                  <Button variant="outline" onClick={() => navigate("/alerts")}>Cancel</Button>
                  <Button onClick={() => handleHighBpFollowupQuestion("high_followup_q4", "high_followup_q5")} className={primaryBtnClass}>
                    Confirm
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {currentStep === "high_followup_q5" && (
        <Card>
          <CardHeader>
            <CardTitle>Have you consumed any alcohol?</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <RadioGroup value={highBpFollowupAnswers.high_followup_q5 || ""} onValueChange={(value) => setHighBpFollowupAnswers({...highBpFollowupAnswers, high_followup_q5: value})} className="flex flex-row gap-6">
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="yes" id="high-followup-q5-yes" />
                  <Label htmlFor="high-followup-q5-yes" className="cursor-pointer">Yes</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="no" id="high-followup-q5-no" />
                  <Label htmlFor="high-followup-q5-no" className="cursor-pointer">No</Label>
                </div>
              </RadioGroup>
              {highBpFollowupAnswers.high_followup_q5 === "yes" && (
                <div className="space-y-2">
                  <Label htmlFor="high-followup-q5-explanation">How much alcohol have you consumed in the last 48 hours?</Label>
                  <Textarea
                    id="high-followup-q5-explanation"
                    placeholder="Please provide details..."
                    value={highBpFollowupExplanations.high_followup_q5 || ""}
                    onChange={(e) => setHighBpFollowupExplanations({...highBpFollowupExplanations, high_followup_q5: e.target.value})}
                    className="min-h-[100px]"
                  />
                </div>
              )}
              <div className="flex justify-between gap-3 pt-4">
                <Button variant="outline" onClick={handleBack} className="flex items-center gap-2">
                  <ArrowLeft className="h-4 w-4" />
                  Back
                </Button>
                <div className="flex gap-3">
                  <Button variant="outline" onClick={() => navigate("/alerts")}>Cancel</Button>
                  <Button onClick={() => handleHighBpFollowupQuestion("high_followup_q5", "high_followup_q6")} className={primaryBtnClass}>
                    Confirm
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {currentStep === "high_followup_q6" && (
        <Card>
          <CardHeader>
            <CardTitle>Have you been smoking?</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <RadioGroup value={highBpFollowupAnswers.high_followup_q6 || ""} onValueChange={(value) => setHighBpFollowupAnswers({...highBpFollowupAnswers, high_followup_q6: value})} className="flex flex-row gap-6">
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="yes" id="high-followup-q6-yes" />
                  <Label htmlFor="high-followup-q6-yes" className="cursor-pointer">Yes</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="no" id="high-followup-q6-no" />
                  <Label htmlFor="high-followup-q6-no" className="cursor-pointer">No</Label>
                </div>
              </RadioGroup>
              {highBpFollowupAnswers.high_followup_q6 === "yes" && (
                <div className="space-y-2">
                  <Label htmlFor="high-followup-q6-explanation">How much more than usual have you been smoking? What have you been smoking?</Label>
                  <Textarea
                    id="high-followup-q6-explanation"
                    placeholder="Please provide details..."
                    value={highBpFollowupExplanations.high_followup_q6 || ""}
                    onChange={(e) => setHighBpFollowupExplanations({...highBpFollowupExplanations, high_followup_q6: e.target.value})}
                    className="min-h-[100px]"
                  />
                </div>
              )}
              <div className="flex justify-between gap-3 pt-4">
                <Button variant="outline" onClick={handleBack} className="flex items-center gap-2">
                  <ArrowLeft className="h-4 w-4" />
                  Back
                </Button>
                <div className="flex gap-3">
                  <Button variant="outline" onClick={() => navigate("/alerts")}>Cancel</Button>
                  <Button onClick={() => handleHighBpFollowupQuestion("high_followup_q6", "high_followup_q7")} className={primaryBtnClass}>
                    Confirm
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {currentStep === "high_followup_q7" && (
        <Card>
          <CardHeader>
            <CardTitle>Have you used an anti-inflammatory drug, such as ibuprofen?</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <RadioGroup value={highBpFollowupAnswers.high_followup_q7 || ""} onValueChange={(value) => setHighBpFollowupAnswers({...highBpFollowupAnswers, high_followup_q7: value})} className="flex flex-row gap-6">
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="yes" id="high-followup-q7-yes" />
                  <Label htmlFor="high-followup-q7-yes" className="cursor-pointer">Yes</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="no" id="high-followup-q7-no" />
                  <Label htmlFor="high-followup-q7-no" className="cursor-pointer">No</Label>
                </div>
              </RadioGroup>
              {highBpFollowupAnswers.high_followup_q7 === "yes" && (
                <div className="space-y-2">
                  <Label htmlFor="high-followup-q7-explanation">What have you been taking? How much have you been taking?</Label>
                  <Textarea
                    id="high-followup-q7-explanation"
                    placeholder="What have you been taking? How much have you been taking?..."
                    value={highBpFollowupExplanations.high_followup_q7 || ""}
                    onChange={(e) => setHighBpFollowupExplanations({...highBpFollowupExplanations, high_followup_q7: e.target.value})}
                    className="min-h-[100px]"
                  />
                </div>
              )}
              <div className="flex justify-between gap-3 pt-4">
                <Button variant="outline" onClick={handleBack} className="flex items-center gap-2">
                  <ArrowLeft className="h-4 w-4" />
                  Back
                </Button>
                <div className="flex gap-3">
                  <Button variant="outline" onClick={() => navigate("/alerts")}>Cancel</Button>
                  <Button onClick={() => handleHighBpFollowupQuestion("high_followup_q7", "high_followup_q8")} className={primaryBtnClass}>
                    Confirm
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {currentStep === "high_followup_q8" && (
        <Card>
          <CardHeader>
            <CardTitle>Are you on any new medications, including prescription, over-the-counter, or illicit drugs (diuretics, decongestants, steroids, stimulants)?</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <RadioGroup value={highBpFollowupAnswers.high_followup_q8 || ""} onValueChange={(value) => setHighBpFollowupAnswers({...highBpFollowupAnswers, high_followup_q8: value})} className="flex flex-row gap-6">
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="yes" id="high-followup-q8-yes" />
                  <Label htmlFor="high-followup-q8-yes" className="cursor-pointer">Yes</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="no" id="high-followup-q8-no" />
                  <Label htmlFor="high-followup-q8-no" className="cursor-pointer">No</Label>
                </div>
              </RadioGroup>
              {highBpFollowupAnswers.high_followup_q8 === "yes" && (
                <div className="space-y-2">
                  <Label htmlFor="high-followup-q8-explanation">Please Explain</Label>
                  <Textarea
                    id="high-followup-q8-explanation"
                    placeholder="Please Explain..."
                    value={highBpFollowupExplanations.high_followup_q8 || ""}
                    onChange={(e) => setHighBpFollowupExplanations({...highBpFollowupExplanations, high_followup_q8: e.target.value})}
                    className="min-h-[100px]"
                  />
                </div>
              )}
              <div className="flex justify-between gap-3 pt-4">
                <Button variant="outline" onClick={handleBack} className="flex items-center gap-2">
                  <ArrowLeft className="h-4 w-4" />
                  Back
                </Button>
                <div className="flex gap-3">
                  <Button variant="outline" onClick={() => navigate("/alerts")}>Cancel</Button>
                  <Button onClick={() => handleHighBpFollowupQuestion("high_followup_q8", "high_followup_q9")} className={primaryBtnClass}>
                    Confirm
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {currentStep === "high_followup_q9" && (
        <Card>
          <CardHeader>
            <CardTitle>(If not using a connected scale) Have you gained weight recently?</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <RadioGroup value={highBpFollowupAnswers.high_followup_q9 || ""} onValueChange={(value) => setHighBpFollowupAnswers({...highBpFollowupAnswers, high_followup_q9: value})} className="flex flex-row gap-6">
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="yes" id="high-followup-q9-yes" />
                  <Label htmlFor="high-followup-q9-yes" className="cursor-pointer">Yes</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="no" id="high-followup-q9-no" />
                  <Label htmlFor="high-followup-q9-no" className="cursor-pointer">No</Label>
                </div>
              </RadioGroup>
              {highBpFollowupAnswers.high_followup_q9 === "yes" && (
                <div className="space-y-2">
                  <Label htmlFor="high-followup-q9-explanation">How much weight have you gained?</Label>
                  <Textarea
                    id="high-followup-q9-explanation"
                    placeholder="How much weight have you gained?..."
                    value={highBpFollowupExplanations.high_followup_q9 || ""}
                    onChange={(e) => setHighBpFollowupExplanations({...highBpFollowupExplanations, high_followup_q9: e.target.value})}
                    className="min-h-[100px]"
                  />
                </div>
              )}
              <div className="flex justify-between gap-3 pt-4">
                <Button variant="outline" onClick={handleBack} className="flex items-center gap-2">
                  <ArrowLeft className="h-4 w-4" />
                  Back
                </Button>
                <div className="flex gap-3">
                  <Button variant="outline" onClick={() => navigate("/alerts")}>Cancel</Button>
                  <Button onClick={() => handleHighBpFollowupQuestion("high_followup_q9", "high_followup_q10")} className={primaryBtnClass}>
                    Confirm
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {currentStep === "high_followup_q10" && (
        <Card>
          <CardHeader>
            <CardTitle>How much exercise have you had?</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="high-followup-q10-exercise">Please describe how much exercise you have had:</Label>
                <Textarea
                  id="high-followup-q10-exercise"
                  placeholder="Please describe how much exercise you have had..."
                  value={highBpFollowupAnswers.high_followup_q10 || ""}
                  onChange={(e) => setHighBpFollowupAnswers({...highBpFollowupAnswers, high_followup_q10: e.target.value})}
                  className="min-h-[120px]"
                />
              </div>
              <div className="flex justify-between gap-3 pt-4">
                <Button variant="outline" onClick={handleBack} className="flex items-center gap-2">
                  <ArrowLeft className="h-4 w-4" />
                  Back
                </Button>
                <div className="flex gap-3">
                  <Button variant="outline" onClick={() => navigate("/alerts")}>Cancel</Button>
                  <Button onClick={() => handleHighBpFollowupQuestion("high_followup_q10", "high_followup_q11")} className={primaryBtnClass}>
                    Confirm
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {currentStep === "high_followup_q11" && (
        <Card>
          <CardHeader>
            <CardTitle>Any changes to your regular exercise program?</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <RadioGroup value={highBpFollowupAnswers.high_followup_q11 || ""} onValueChange={(value) => setHighBpFollowupAnswers({...highBpFollowupAnswers, high_followup_q11: value})} className="flex flex-row gap-6">
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="yes" id="high-followup-q11-yes" />
                  <Label htmlFor="high-followup-q11-yes" className="cursor-pointer">Yes</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="no" id="high-followup-q11-no" />
                  <Label htmlFor="high-followup-q11-no" className="cursor-pointer">No</Label>
                </div>
              </RadioGroup>
              {highBpFollowupAnswers.high_followup_q11 === "yes" && (
                <div className="space-y-2">
                  <Label htmlFor="high-followup-q11-explanation">Please Explain</Label>
                  <Textarea
                    id="high-followup-q11-explanation"
                    placeholder="Please Explain..."
                    value={highBpFollowupExplanations.high_followup_q11 || ""}
                    onChange={(e) => setHighBpFollowupExplanations({...highBpFollowupExplanations, high_followup_q11: e.target.value})}
                    className="min-h-[100px]"
                  />
                </div>
              )}
              <div className="flex justify-between gap-3 pt-4">
                <Button variant="outline" onClick={handleBack} className="flex items-center gap-2">
                  <ArrowLeft className="h-4 w-4" />
                  Back
                </Button>
                <div className="flex gap-3">
                  <Button variant="outline" onClick={() => navigate("/alerts")}>Cancel</Button>
                  <Button onClick={() => handleHighBpFollowupQuestion("high_followup_q11", "high_followup_q12")} className={primaryBtnClass}>
                    Confirm
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {currentStep === "high_followup_q12" && (
        <Card>
          <CardHeader>
            <CardTitle>How do you feel?</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="high-followup-q12-feelings">Please describe how you feel:</Label>
                <Textarea
                  id="high-followup-q12-feelings"
                  placeholder="Please describe how you feel..."
                  value={highBpFollowupAnswers.high_followup_q12 || ""}
                  onChange={(e) => setHighBpFollowupAnswers({...highBpFollowupAnswers, high_followup_q12: e.target.value})}
                  className="min-h-[120px]"
                />
              </div>
              <div className="flex justify-between gap-3 pt-4">
                <Button variant="outline" onClick={handleBack} className="flex items-center gap-2">
                  <ArrowLeft className="h-4 w-4" />
                  Back
                </Button>
                <div className="flex gap-3">
                  <Button variant="outline" onClick={() => navigate("/alerts")}>Cancel</Button>
                  <Button 
                    onClick={() => handleHighBpFollowupQuestion("high_followup_q12", "high_q5")} 
                    className={primaryBtnClass}
                  >
                    Confirm
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Nursing Note Alert Dialog */}
      {/* <Dialog open={showNursingNoteDialog} onOpenChange={setShowNursingNoteDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Alert</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm">
              You have now completed your triage assessment. would you like to enter nursing note for this?
            </p>
            <div className="flex justify-end gap-3 pt-4">
              <Button
                variant="outline"
                onClick={() => handleNursingNoteResponse("no")}
                className="border-red-500 text-red-500 hover:bg-red-50"
              >
                No
              </Button>
              <Button
                onClick={() => handleNursingNoteResponse("yes")}
                className="bg-amber-600 hover:bg-amber-700 text-white"
              >
                Yes
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog> */}
    </div>
  );
};

export default BloodPressureFlow;

